import { Box, Chip, Grid, LinearProgress, Typography } from "@mui/material";
import { User } from "../../utils";
import React, { useEffect, useState } from "react";
import MetricCard from "../sequences/SequenceMetricsCard";

import {
  Bot,
  Calendar,
  Clock,
  Database,
  DollarSign,
  FileText,
  Image,
  Inbox,
  List,
  Megaphone,
  MessageSquare,
  MousePointerClick,
  Rocket,
  Send,
  TrendingUp,
  Users,
} from "lucide-react";
import { BarChart } from "@mui/x-charts/BarChart";
import axios from "axios";
import { FEATURES } from "../../constants/features";
import { useFeatures } from "../../context/FeatureContext";
import { hostname, parseError } from "../../utils";
import SequenceActionsDialog from "../sequences/dialogs/SequenceActionsDialog";
import SequenceMetricsCards from "../../components/SequenceMetricsCards";
import FeatureUsageChart from "./FeatureUsageChart";
import UsageLeaderboard from "./UsageLeaderboard";
import EmployeeLeaderboard from "./EmployeeLeaderboard";
import ActivityTimeline from "../../components/ActivityTimeline";
import ContentMetricsBar from "../content/ContentMetricsBar";
import InboxMetricsBar from "../inbox/InboxMetricsBar";
import ListMetricsBar from "../lists/ListMetricsBar";
import AgentMetricsBar from "../agents/AgentMetricsBar";
import MarketingCampaignsMetricsBar from "../marketing-campaigns/MarketingCampaignsMetricsBar";
import { DEMO_MODE_KEY } from "../../constants";
import {
  DEMO_AGENT_STATS,
  DEMO_CONTENT_STATS,
  DEMO_HOURS_SAVED,
  DEMO_INBOX_STATS,
  DEMO_LIST_STATS,
  DEMO_MARKETING_CAMPAIGN_METRICS,
  DEMO_SEQUENCE_METRICS,
} from "../../constants/demoData";

export default ({
  mode,
  currentUser,
  companyId,
  token,
  getToken,
  eventListener,
  openDialog,
  subScreenName,
  instanceId,
}: {
  mode: string;
  currentUser: any | User;
  companyId: string;
  token: any | string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  openDialog: (dialog: any) => void;
  subScreenName: string | undefined;
  instanceId: string | undefined;
}) => {
  const { isFeatureHidden } = useFeatures();
  const [loading, setLoading] = React.useState(true);
  const [metrics, setMetrics] = React.useState<any>(null);
  const [usageSeries, setUsageSeries] = useState<Array<any>>([]);
  const [usageLables, setUsageLabels] = useState<Array<string>>([]);
  const [usageScope, setUsageScope] = useState<"company" | "platform">(
    "company"
  );
  const [approvalsOpen, setApprovalsOpen] = useState(false);
  const [listStats, setListStats] = useState<any>(null);
  const [inboxStats, setInboxStats] = useState<any>(null);
  const [contentStats, setContentStats] = useState<any>(null);
  const [agentStats, setAgentStats] = useState<any>(null);
  const [marketingStats, setMarketingStats] = useState<any>(null);
  const [hoursSaved, setHoursSaved] = useState<any>(null);

  const loadMetrics = async () => {
    setLoading(true);
    if (localStorage.getItem(DEMO_MODE_KEY) === "true") {
      setMetrics(DEMO_SEQUENCE_METRICS);
      return;
    }
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/sequences/metrics`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setMetrics(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const loadUsageChart = async () => {
    setLoading(true);
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/usage/histogram/last_30_days`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        const seriesWithIds = res.data.series.map(
          (series: any, index: number) => ({
            ...series,
            id: series.id || `series-${index}`,
            label: series.label || `Series ${index + 1}`,
          })
        );
        setUsageSeries(seriesWithIds);
        setUsageLabels(res.data.labels);
        setUsageScope(res.data.scope || "company");
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const isDemoMode = () => localStorage.getItem(DEMO_MODE_KEY) === "true";

  const loadListStats = async () => {
    if (isDemoMode()) {
      setListStats(DEMO_LIST_STATS);
      return;
    }
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/lists/statistics`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setListStats(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const loadInboxStats = async () => {
    if (isDemoMode()) {
      setInboxStats(DEMO_INBOX_STATS);
      return;
    }
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/inboxes/statistics`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setInboxStats(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const loadAgentStats = async () => {
    if (isDemoMode()) {
      setAgentStats(DEMO_AGENT_STATS);
      return;
    }
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/agents/statistics`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setAgentStats(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const loadMarketingStats = async () => {
    if (isDemoMode()) {
      setMarketingStats(DEMO_MARKETING_CAMPAIGN_METRICS);
      return;
    }
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/marketing-campaigns/statistics`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setMarketingStats(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const loadContentStats = async () => {
    if (isDemoMode()) {
      setContentStats(DEMO_CONTENT_STATS);
      return;
    }
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/content/statistics`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setContentStats(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const loadHoursSaved = async () => {
    if (isDemoMode()) {
      setHoursSaved(DEMO_HOURS_SAVED);
      return;
    }
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/usage/hours-saved`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setHoursSaved(res.data);
      })
      .catch((err) => {
        // Don't show error for hours saved - it's optional
        console.error("Error loading hours saved:", err);
      });
  };

  const getData = async () => {
    setLoading(true);
    const f1 = loadMetrics();
    const f2 = loadUsageChart();
    const f3 = loadListStats();
    const f4 = loadInboxStats();
    const f5 = loadContentStats();
    const f6 = loadHoursSaved();
    const f7 = loadAgentStats();
    const f8 = loadMarketingStats();
    await Promise.all([f1, f2, f3, f4, f5, f6, f7, f8]);
    setLoading(false);
  };

  useEffect(() => {
    if (currentUser.uid && companyId) {
      getData();
    }
  }, [currentUser.uid, companyId]);

  if (loading || !metrics || !metrics.totals) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  return (
    <Box>
      {/* Hours Saved Section - Always show */}
      <Box data-tutorial="hours-saved">
        <Typography sx={{ mt: 2 }} variant="h6" gutterBottom>
          Time Saved
        </Typography>
      </Box>
      <Grid container spacing={1} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Clock size={24} />}
            title="Hours Saved"
            value={hoursSaved ? `${hoursSaved.totalHours} hrs` : "0 hrs"}
            tooltip="Total hours saved through automation in the last 30 days"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<DollarSign size={24} />}
            title="Value Saved"
            value={
              hoursSaved ? `$${hoursSaved.valueSaved.toLocaleString()}` : "$0"
            }
            tooltip="Calculated at $50/hour average employee cost"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<TrendingUp size={24} />}
            title="Total Actions"
            value={
              hoursSaved
                ? Object.values(hoursSaved.breakdown).reduce(
                    (sum: number, item: any) => sum + item.count,
                    0
                  )
                : 0
            }
            tooltip="Total number of automated actions performed"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<Bot size={24} />}
            title="Action Types"
            value={hoursSaved ? Object.keys(hoursSaved.breakdown).length : 0}
            tooltip={
              hoursSaved && Object.keys(hoursSaved.breakdown).length > 0
                ? `Action types: ${Object.values(hoursSaved.breakdown)
                    .map((item: any) => `${item.label} (${item.count})`)
                    .join(", ")}`
                : "Number of different automation types used"
            }
          />
        </Grid>
      </Grid>

      {!isFeatureHidden(FEATURES.SEQUENCE) && (
        <>
          <Typography sx={{ mt: 4 }} variant="h6" gutterBottom>
            Sequence Performance
          </Typography>
          <SequenceMetricsCards
            totals={metrics.totals}
            showPendingApprovals={true}
            onPendingApprovalsClick={() => setApprovalsOpen(true)}
          />
          <ActivityTimeline
            sequenceId={null}
            companyId={companyId}
            getToken={getToken}
            title="Sequence Activity Timeline (Last 3 Days)"
            enabled={!!currentUser.uid}
          />
        </>
      )}

      {!isFeatureHidden(FEATURES.SMART_LIST) && listStats && (
        <>
          <Typography variant="h6" gutterBottom>
            Smart Lists
          </Typography>
          <Grid container spacing={1} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<List size={24} />}
                title="Total Lists"
                value={listStats.total_lists}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Database size={24} />}
                title="Total Rows"
                value={listStats.total_rows}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Bot size={24} />}
                title="Active Automations"
                value={listStats.active_automations}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Rocket size={24} />}
                title="Actions Run (30d)"
                value={listStats.total_executions}
                percentage={listStats.success_rate}
              />
            </Grid>
          </Grid>
          <ListMetricsBar
            companyId={companyId}
            getToken={getToken}
            showMetrics={false}
          />
        </>
      )}

      {!isFeatureHidden(FEATURES.INBOX) && inboxStats && (
        <>
          <Typography variant="h6" gutterBottom>
            Inbox
          </Typography>
          <Grid container spacing={1} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Inbox size={24} />}
                title="Total Inboxes"
                value={inboxStats.total_inboxes}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<MessageSquare size={24} />}
                title="Messages (30d)"
                value={inboxStats.recent_messages}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Users size={24} />}
                title="Unique Senders"
                value={inboxStats.unique_senders}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Calendar size={24} />}
                title="Messages Today"
                value={inboxStats.messages_today}
              />
            </Grid>
          </Grid>
          <InboxMetricsBar
            companyId={companyId}
            getToken={getToken}
            showMetrics={false}
            groupBy="type"
          />
        </>
      )}

      {!isFeatureHidden(FEATURES.AGENT) && agentStats && (
        <>
          <Typography variant="h6" gutterBottom>
            Agents
          </Typography>
          <Grid container spacing={1} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Bot size={24} />}
                title="Total Agents"
                value={agentStats.total_agents}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<MessageSquare size={24} />}
                title="Conversations (7d)"
                value={agentStats.recent_conversations}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Send size={24} />}
                title="Messages (7d)"
                value={agentStats.recent_messages}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Calendar size={24} />}
                title="Conversations Today"
                value={agentStats.conversations_today}
              />
            </Grid>
          </Grid>
          <AgentMetricsBar
            companyId={companyId}
            getToken={getToken}
            showMetrics={false}
          />
        </>
      )}

      {!isFeatureHidden(FEATURES.CONTENT_ENGINE) && contentStats && (
        <>
          <Typography variant="h6" gutterBottom>
            Content Engine
          </Typography>
          <Grid container spacing={1} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<FileText size={24} />}
                title="Total Content"
                value={contentStats.total_content}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Send size={24} />}
                title="Published"
                value={contentStats.published_content}
                percentage={contentStats.publishing_rate}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<FileText size={24} />}
                title="Recent (30d)"
                value={contentStats.recent_content}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Image size={24} />}
                title="Images Generated"
                value={contentStats.total_images}
              />
            </Grid>
          </Grid>
          <ContentMetricsBar companyId={companyId} getToken={getToken} showMetrics={false} />
        </>
      )}

      {!isFeatureHidden(FEATURES.MARKETING_CAMPAIGN_EMAIL) && marketingStats && (
        <>
          <Typography variant="h6" gutterBottom>
            Marketing Campaigns
          </Typography>
          <Grid container spacing={1} mb={2}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Megaphone size={24} />}
                title="Total Campaigns"
                value={marketingStats.totalCampaigns}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<Send size={24} />}
                title="Emails Sent"
                value={marketingStats.totalSent}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<TrendingUp size={24} />}
                title="Open Rate"
                value={`${((marketingStats.openRate || 0) * 100).toFixed(1)}%`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                icon={<MousePointerClick size={24} />}
                title="Click Rate"
                value={`${((marketingStats.clickRate || 0) * 100).toFixed(1)}%`}
              />
            </Grid>
          </Grid>
          <MarketingCampaignsMetricsBar
            companyId={companyId}
            getToken={getToken}
            showMetrics={false}
          />
        </>
      )}

      <Box sx={{ mt: 4 }} data-tutorial="company-leaderboard">
        <UsageLeaderboard
          companyId={companyId}
          getToken={getToken}
          eventListener={eventListener}
        />
      </Box>

      {!isFeatureHidden(FEATURES.EMPLOYEE) && (
        <Box sx={{ mt: 4 }}>
          <EmployeeLeaderboard
            companyId={companyId}
            getToken={getToken}
            eventListener={eventListener}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Credit Usage
        </Typography>
        {usageScope === "platform" && (
          <Chip
            label="Platform-wide"
            color="primary"
            size="small"
            variant="outlined"
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {usageScope === "platform"
          ? "Showing usage across all companies (last 30 days)"
          : "Showing usage for this company (last 30 days)"}
      </Typography>
      <Box data-tutorial="usage-chart">
        <BarChart
          height={500}
          series={usageSeries}
          slotProps={{ legend: { hidden: true } }}
          xAxis={[{ data: usageLables, scaleType: "band" }]}
        />
      </Box>

      <Box data-tutorial="feature-breakdown">
        <FeatureUsageChart
          companyId={companyId}
          getToken={getToken}
          eventListener={eventListener}
        />
      </Box>

      <SequenceActionsDialog
        open={approvalsOpen}
        onClose={() => setApprovalsOpen(false)}
        companyId={companyId}
        sequence={null}
        getToken={getToken}
        initialFilter="unapproved"
      />
    </Box>
  );
};
